import LocalizedLink from '@components/Localization/LocalizedLink'
import { Button } from '@components/rolex-ui/Button'
import { Text } from '@components/rolex-ui/Text'
import { Field } from '@components/rolex-ui/form/Field'
import { Gender } from '@generated'
import { cn } from '@lib/cn'
import { countryCodes } from '@lib/countryCodes'
import { SendAMessageDisplayMode } from '@pages/rolex/contact/send-a-message'
import { useFormikContext } from 'formik'
import { useTranslation } from 'next-i18next'
import { FC } from 'react'

type RolexContactFormFieldsProps = {
  displayMode: SendAMessageDisplayMode
  setDisplayMode: (mode: SendAMessageDisplayMode) => void
  backgroundColor?: 'white' | 'lightBeige'
}

export const RolexContactFormFields: FC<RolexContactFormFieldsProps> = ({
  displayMode,
  setDisplayMode,
  backgroundColor = 'white',
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('rolex')
  const context = useFormikContext()
  const values = context.values as any

  return (
    <div>
      {displayMode === 'message' && (
        <div className="flex w-full flex-col items-center">
          <div
            className={cn(
              'mb-8 w-full',
              backgroundColor === 'white' && 'bg-white',
              backgroundColor === 'lightBeige' && 'bg-rolexLightBeige',
            )}
          >
            <Field
              name="message"
              field="textarea"
              label={t('contactForm.message', { ns: 'rolex' })}
              placeholder={t('contactForm.message', { ns: 'rolex' })}
              className="mb-0 pt-0"
            />
          </div>
          <Button
            as="button"
            variant="primary"
            role="button"
            onClick={() => setDisplayMode('other-fields')}
            className={cn(values.message ? 'opacity-100' : 'pointer-events-none opacity-50')}
          >
            {t('next', { ns: 'rolex' })}
          </Button>
        </div>
      )}
      {displayMode === 'other-fields' && (
        <div className="relative">
          <div className="mb-4 grid grid-cols-12 gap-x-6">
            <Field
              name="gender"
              className="col-span-6 md:col-span-2"
              label={t('contactForm.salutation', { ns: 'rolex' })}
              field="select"
              placeholder=" "
            >
              <option />
              <option value={Gender.Male}>{t('contactForm.mr', { ns: 'rolex' })}</option>
              <option value={Gender.Female}>{t('contactForm.mrs', { ns: 'rolex' })}</option>
            </Field>
            <Field
              name="firstName"
              label={t('contactForm.firstName', { ns: 'rolex' })}
              field="input"
              placeholder=" "
              className="col-span-6 col-start-7 md:col-span-4 md:col-start-3"
            />
            <Field
              name="lastName"
              label={t('contactForm.lastName', { ns: 'rolex' })}
              field="input"
              placeholder=" "
              className="col-span-12 md:col-span-6 md:col-start-7"
            />
          </div>
          <div className="mb-4 grid grid-cols-12 gap-6">
            <Field
              name="email"
              label={t('contactForm.email', { ns: 'rolex' })}
              field="input"
              placeholder=" "
              className="col-span-12 md:col-span-6"
            />
            <Field
              name="countryCode"
              label={t('contactForm.countryCode', { ns: 'rolex' })}
              field="select"
              className="col-span-6 md:col-span-2 md:col-start-7"
            >
              {countryCodes.map((code, index) => (
                <option value={code.value} key={index} data-countrycode={code.countrycode}>
                  {code.text}
                </option>
              ))}
            </Field>
            <Field
              name="phone"
              label={t('contactForm.phone', { ns: 'rolex' })}
              field="input"
              placeholder=" "
              className="col-span-6 col-start-7 md:col-span-4 md:col-start-9"
            />
          </div>
          <div className="mb-2 grid grid-cols-12 gap-6">
            <Field
              name="location"
              label={t('contactForm.location', { ns: 'rolex' })}
              field="select"
              placeholder={t('contactForm.location', { ns: 'rolex' })}
              className="col-span-6"
            >
              <option value="" />
              <option value="amsterdam">Amsterdam</option>
              <option value="singapore">Singapore</option>
            </Field>
            <Field
              disabled={values.location === ''}
              name="store"
              label={t('contactForm.store', { ns: 'rolex' })}
              field="select"
              placeholder=" "
              className="col-span-6 col-start-7"
            >
              {values.location === 'singapore' ? (
                <>
                  <option value="" />
                  <option>GASSAN Singapore Changi Airport | Terminal 1</option>
                  <option>GASSAN Singapore Changi Airport | Terminal 4</option>
                  <option>GASSAN Rolex Boutique Singapore Changi Airport | Terminal 3</option>
                </>
              ) : (
                <>
                  <option value="" />
                  <option>Rolex Boutique HOUSE of GASSAN</option>
                  <option>Rolex Corner GASSAN Dam Square </option>
                  <option>Rolex Corner GASSAN Rotterdam</option>
                  <option>Rolex Boutique GASSAN Schiphol</option>
                </>
              )}
            </Field>
          </div>
          <div className="mb-8">
            <Field
              name="agree"
              label={
                <Text variant="fixed16" as="span" weight="light">
                  *{t('contactForm.privacyNotice', { ns: 'rolex' })}
                  <LocalizedLink href={t('privacyPolicy', { ns: 'navigation' })}>
                    <a
                      style={{ fontWeight: 'bold' }}
                      href={t('privacyPolicy', { ns: 'navigation' })}
                    >
                      {t('contactForm.privacyPolicy', { ns: 'rolex' })}
                    </a>
                  </LocalizedLink>
                  {t('contactForm.privacyNoticeAndOur', { ns: 'rolex' })}
                  <LocalizedLink href={t('cookieStatement', { ns: 'navigation' })}>
                    <a
                      style={{ fontWeight: 'bold' }}
                      href={t('cookieStatement', { ns: 'navigation' })}
                    >
                      {t('contactForm.cookieStatement', { ns: 'rolex' })}
                    </a>
                  </LocalizedLink>
                </Text>
              }
              field="checkbox"
            />
            <Field
              name="subscribe"
              label={
                <Text variant="fixed16" as="span" weight="light">
                  {t('contactForm.subscribe', { ns: 'rolex' })}
                </Text>
              }
              field="checkbox"
            />
          </div>
          <div className="flex w-full justify-center">
            <Button
              as="button"
              variant="primary"
              type="submit"
              status={context.isSubmitting ? 'disabled' : 'idle'}
            >
              {context.isSubmitting
                ? t('contactForm.sending', { ns: 'rolex' })
                : t('contactForm.sendMessage', { ns: 'rolex' })}
            </Button>
          </div>
        </div>
      )}
      {displayMode === 'success' && (
        <div className="mt-4 flex justify-center md:mt-8">
          <Button variant="primary" as="link" role="button" href={`/${language}/rolex`}>
            {t('done', { ns: 'rolex' })}
          </Button>
        </div>
      )}
    </div>
  )
}
